import {FC, useEffect, useState} from 'react';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridColDef,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridValueGetterParams
} from '@mui/x-data-grid';
// custom
import SimpleSpinner from '../loaders/spinners/SimpleSpinner';
import { IPayment } from '../../common/state/payments/payment';

const ContainerStyled = styled.section`
    width: 100%;
    table {
        width: 100%
    }
`;

type IPaymentOverviewTableProps = {
    list: IPayment[] | undefined    
};

type IPaymentTableProps = Omit<IPayment, 'id'> & { 'id': number };

type MuiTableProps = {
  list: IPaymentTableProps[];
};


// headers for export to csv
const CustomToolbar: FC = () => {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end', backgroundColor: '#f5ebe0', '& .MuiButton-root, & .MuiButtonBase-root': { color: 'black' } }}>
      <GridToolbarColumnsButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
      <GridToolbarFilterButton placeholder="" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} onResize={undefined} onResizeCapture={undefined} />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};


const MuiTableAdmin: FC<MuiTableProps> = ({ list }) => {

  // material columns
  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 10, headerClassName: 'custom-header' },
    {
      field: 'description',
      headerName: 'Description',
      sortable: true,
      headerClassName: 'custom-header',
      flex: 1
    },
    {
      field: 'amount',
      headerName: 'Amount',
      sortable: true,
      headerClassName: 'custom-header',
      width: 200
    },

    {
      field: 'currency',
      headerName: 'Currency',
      headerClassName: 'custom-header',
      width: 200,
      sortable: true,
      valueGetter: (data: GridValueGetterParams) => {
        return data.row.currency.toUpperCase();
      }
    },
    {
      field: 'created_date',
      type: 'date',
      headerName: 'Created On',
      headerClassName: 'custom-header',
      sortable: true,
      width: 200,
      valueGetter: (data: GridValueGetterParams) => {
        return new Date(data.row.created_date);
      }
    },
  ];

  return (<Box sx={{ minHeight: 500, width: '100%' }}>
    <DataGrid
      style={{ fontSize: 15 }}
      rows={list}
      columns={columns}
      components={{ Toolbar: CustomToolbar }}
      sx={{
        '.custom-header': {
          backgroundColor: '#e3d5ca',
          fontWeight: 600,
          color: 'black',
        },
      }}
    />
  </Box>
  );
};

const PaymentOverviewMuiTableAdmin : FC <IPaymentOverviewTableProps> = ({list}) => {
    // states
    const [title, setTitle] = useState<string>('');
    const [subTitle, setSubTitle] = useState<string>('Please wait...');    
    const [paymentTableList, setPaymentTableList] = useState<IPaymentTableProps[]>([]);

    // lifecycle
    useEffect(()=>{
        if (list && list.length > 0) {
            const tableList: IPaymentTableProps[] = list.map((item: IPayment, idx: number) => ({ ...item, id: idx + 1 }));
            setPaymentTableList(tableList);
            setTitle('Payment List Detail');
            setSubTitle('Payment breakdown with their corresponding amount...');
        } else if (list && list.length === 0) {
            setTitle('No Payments');
            setSubTitle('There are currently no payment info available. Click on + to add a payment');
        }
    },[list]);

    return <ContainerStyled
        className="mt-2 relative bg-gray-100 dark:bg-gray-900 rounded-lg 
                     flex flex-col justify-start items-start shadow-lg 
                    p-4 overflow-hidden overflow-scroll">
        <div role="status"></div>
        <h2 className="text-gray-700 dark:text-teal-400 text-2xl font-medium mb-2">{title}</h2>
        <p className="text-gray-700 dark:text-gray-300 dartext-base mb-4">
            {subTitle}
        </p>
        <SimpleSpinner display={`inline-block`} loading={!list} title={'Loading payments...'}/>
            {Boolean(list) && (list as IPayment[]).length > 0 && <MuiTableAdmin list={paymentTableList} />}
    </ContainerStyled>;
};

export default PaymentOverviewMuiTableAdmin;
